import axios, { AxiosInstance, AxiosError } from 'axios';
import { API_CONFIG, API_ENDPOINTS } from '../config';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: async (data: { name: string; email: string; password: string; phone?: string }) => {
    const response = await api.post(API_ENDPOINTS.REGISTER, data);
    return response.data;
  },

  login: async (data: { email: string; password: string }) => {
    const response = await api.post(API_ENDPOINTS.LOGIN, data);
    return response.data;
  },

  forgotPassword: async (data: { email: string }) => {
    const response = await api.post(API_ENDPOINTS.FORGOT_PASSWORD, data);
    return response.data;
  },

  resetPassword: async (data: { token: string; new_password: string }) => {
    const response = await api.post(API_ENDPOINTS.RESET_PASSWORD, data);
    return response.data;
  },
};

// User API
export const userAPI = {
  getProfile: async () => {
    const response = await api.get(API_ENDPOINTS.PROFILE);
    return response.data;
  },

  updateProfile: async (data: { name?: string; phone?: string }) => {
    const response = await api.put(API_ENDPOINTS.UPDATE_PROFILE, data);
    return response.data;
  },

  getUsage: async () => {
    const response = await api.get(API_ENDPOINTS.USAGE);
    return response.data;
  },
};

// API Keys API
export const apiKeysAPI = {
  list: async () => {
    const response = await api.get(API_ENDPOINTS.API_KEYS);
    return response.data;
  },

  create: async (data: {
    name: string;
    url?: string;
    bucket_type?: string;
    bucket_name?: string;
    bucket_region?: string;
    secret_key?: string;
  }) => {
    const response = await api.post(API_ENDPOINTS.API_KEYS, data);
    return response.data;
  },

  update: async (id: string, data: { name?: string; status?: string }) => {
    const response = await api.put(API_ENDPOINTS.API_KEY_BY_ID(id), data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(API_ENDPOINTS.API_KEY_BY_ID(id));
    return response.data;
  },
};

// Screenshots API
export const screenshotsAPI = {
  list: async (page: number = 1, limit: number = 20) => {
    const response = await api.get(API_ENDPOINTS.SCREENSHOTS, {
      params: { page, limit },
    });
    return response.data;
  },

  capture: async (apiKey: string, data: {
    url: string;
    viewport?: { width: number; height: number };
    format?: string;
    quality?: number;
    fullPage?: boolean;
    delay?: number;
  }) => {
    const response = await axios.post(
      `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CAPTURE_SCREENSHOT}`,
      data,
      {
        headers: {
          'X-API-Key': apiKey,
        },
      }
    );
    return response.data;
  },
};

// Payments API
export const paymentsAPI = {
  createOrder: async (data: { plan: string }) => {
    const response = await api.post(API_ENDPOINTS.CREATE_ORDER, data);
    return response.data;
  },

  verifyPayment: async (data: {
    order_id: string;
    payment_id: string;
    signature: string;
  }) => {
    const response = await api.post(API_ENDPOINTS.VERIFY_PAYMENT, data);
    return response.data;
  },

  getHistory: async (page: number = 1, limit: number = 20) => {
    const response = await api.get(API_ENDPOINTS.PAYMENT_HISTORY, {
      params: { page, limit },
    });
    return response.data;
  },
};

// Health check
export const healthCheck = async () => {
  const response = await api.get(API_ENDPOINTS.HEALTH);
  return response.data;
};

export default api;

